// Keeps a read book's PDF on this browser (Cache Storage `book-pdf-v2`), keyed by (postId, fileId): file-stc objects are
// immutable, so a new fileId means a new PDF version. Every copy is AES-GCM encrypted (bookPdfKey: "BPC1" + IV +
// ciphertext) and decrypted in memory only; the plaintext never touches Cache Storage, and sign-out deletes the key.
// An index in localStorage drives least-recently-opened eviction over MAX_BYTES (stored sizes). Without Cache Storage,
// WebCrypto or IndexedDB (private mode, old browser) `available()` is false and the reader falls back to the signed URL.
// The book is never opened from here without a fresh read link first (that is the access check).
import logger from "./logger";
import { cryptoAvailable, deleteKey, getKey, keyStore, seal, unseal } from "./bookPdfKey";

export const CACHE_NAME = "book-pdf-v2";
export const INDEX_KEY = "bookPdfCache.v2.index";
// The plain (unencrypted) cache of the previous build and its index: deleted at app start (main.tsx) or on the first
// available() check, whenever Cache Storage is there, even when WebCrypto / IndexedDB are not (no encrypted cache then).
const OLD_CACHE_NAME = "book-pdf-v1";
const OLD_INDEX_KEY = "bookPdfCache.index";
const MIGRATED_KEY = "bookPdfCache.v2.migrated";
export const MAX_BYTES = 314_572_800; // 300 MB

interface Entry { postId: string; fileId: string; size: number; lastUsed: number }

const requestKey = (postId: string, fileId: string) =>
  `/__book-pdf/${encodeURIComponent(postId)}/${encodeURIComponent(fileId)}`;

function readIndex(): Entry[] {
  try {
    const raw = JSON.parse(localStorage.getItem(INDEX_KEY) ?? "[]");
    return Array.isArray(raw) ? raw.filter((e) => e && typeof e.postId === "string" && typeof e.fileId === "string") : [];
  } catch { return []; }
}
function writeIndex(entries: Entry[]) {
  try { localStorage.setItem(INDEX_KEY, JSON.stringify(entries)); } catch { /* private mode / quota */ }
}

// Every PDF starts with "%PDF" (25 50 44 46). Bytes that do not (an error page, a truncated or damaged copy) are never
// stored, and a cached copy that fails the check after decryption (or fails to decrypt) is removed so the reader
// downloads it again.
export function looksLikePdf(bytes: ArrayBuffer): boolean {
  if (bytes.byteLength < 4) return false;
  const b = new Uint8Array(bytes, 0, 4);
  return b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46;
}

const cachesAvailable = () => typeof caches !== "undefined" && typeof caches.open === "function";
const open = () => caches.open(CACHE_NAME);

// Deletes the old plain cache and its index; needs only Cache Storage. Done once per browser (MIGRATED_KEY) and
// tried at most once per page load (a failure is tried again on the next load).
async function migrateOld() {
  try {
    if (!cachesAvailable() || localStorage.getItem(MIGRATED_KEY)) return;
    localStorage.removeItem(OLD_INDEX_KEY);
    await caches.delete(OLD_CACHE_NAME);
    localStorage.setItem(MIGRATED_KEY, "1");
  } catch (e) {
    logger.warn("[bookPdfCache] removing the old cache failed", e instanceof Error ? e.name : "error");
  }
}
let migration: Promise<void> | null = null;
/** Starts (once per page load) the removal of the old plain cache; resolves when done. Never rejects. */
const migrate = (): Promise<void> => (migration ??= migrateOld());
/** Tests: the next migrate() runs again. */
export function resetMigrationForTests() { migration = null; }

const available = () => {
  void migrate();
  return cachesAvailable() && cryptoAvailable() && keyStore() != null;
};

// Every cache operation runs one after another (a promise chain), and the localStorage index is re-read just before
// each write, so quick double opens or a second tab cannot overwrite each other's index entries.
let queue: Promise<unknown> = Promise.resolve();
const serial = <T,>(job: () => Promise<T>): Promise<T> => {
  const run = queue.then(job, job);
  queue = run.catch(() => undefined);
  return run;
};

// clear() / remove() bump these, so a download that started before them cannot write its book back afterwards.
let globalGen = 0;
const postGen = new Map<string, number>();
/** Take before starting a download; hand to put(). */
const stamp = (postId: string) => `${globalGen}:${postGen.get(postId) ?? 0}`;

function get(postId: string, fileId: string): Promise<ArrayBuffer | null> {
  if (!available()) return Promise.resolve(null);
  return serial(async () => {
    await migrate();
    try {
      const hit = await (await open()).match(requestKey(postId, fileId));
      const index = readIndex();
      if (!hit) {
        // Not in Cache Storage (evicted by the browser): drop a stale index entry so the cap does not over-count.
        if (index.some((e) => e.postId === postId && e.fileId === fileId)) {
          writeIndex(index.filter((e) => !(e.postId === postId && e.fileId === fileId)));
        }
        return null;
      }
      const sealed = await hit.arrayBuffer();
      const key = await getKey(false);
      const bytes = key ? await unseal(key, sealed, requestKey(postId, fileId)) : null;
      if (!bytes || !looksLikePdf(bytes)) {
        logger.warn("[bookPdfCache] cached copy is unreadable or not a PDF, removing it");
        await (await open()).delete(requestKey(postId, fileId));
        writeIndex(readIndex().filter((e) => !(e.postId === postId && e.fileId === fileId)));
        return null;
      }
      const fresh = readIndex();
      const entry = fresh.find((e) => e.postId === postId && e.fileId === fileId);
      if (entry) { entry.lastUsed = Date.now(); writeIndex(fresh); }
      else writeIndex([...fresh, { postId, fileId, size: sealed.byteLength, lastUsed: Date.now() }]);
      return bytes;
    } catch (e) {
      logger.warn("[bookPdfCache] read failed", e instanceof Error ? e.name : "error");
      return null;
    }
  });
}

/** Stores the PDF, drops the post's other versions, then evicts the least recently opened books over the cap.
 *  `token` is stamp(postId) from before the download started: if clear()/remove() ran since, nothing is written. */
function put(postId: string, fileId: string, bytes: ArrayBuffer, token: string): Promise<void> {
  if (!available() || bytes.byteLength > MAX_BYTES) return Promise.resolve();
  if (!looksLikePdf(bytes)) {
    logger.warn("[bookPdfCache] download is not a PDF, not storing it");
    return Promise.resolve();
  }
  return serial(async () => {
    await migrate();
    if (token !== stamp(postId)) return;
    try {
      const key = await getKey(true);
      if (!key) throw new Error("no key");
      const sealed = await seal(key, bytes, requestKey(postId, fileId));
      const { size } = sealed;
      const cache = await open();
      for (const old of readIndex().filter((e) => e.postId === postId && e.fileId !== fileId)) {
        await cache.delete(requestKey(old.postId, old.fileId));
      }
      await cache.put(requestKey(postId, fileId), new Response(sealed.body, { headers: { "Content-Type": "application/octet-stream" } }));
      const index = readIndex().filter((e) => e.postId !== postId);
      index.push({ postId, fileId, size, lastUsed: Date.now() });
      let total = index.reduce((sum, e) => sum + e.size, 0);
      const evicted: Entry[] = [];
      for (const e of [...index].sort((a, b) => a.lastUsed - b.lastUsed)) {
        if (total <= MAX_BYTES) break;
        if (e.postId === postId) continue; // never evict the book just stored
        evicted.push(e);
        total -= e.size;
      }
      for (const e of evicted) await cache.delete(requestKey(e.postId, e.fileId));
      const drop = new Set(evicted.map((e) => `${e.postId}/${e.fileId}`));
      const latest = readIndex().filter((e) => e.postId !== postId && !drop.has(`${e.postId}/${e.fileId}`));
      latest.push({ postId, fileId, size, lastUsed: Date.now() });
      writeIndex(latest);
    } catch (e) {
      logger.warn("[bookPdfCache] write failed", e instanceof Error ? e.name : "error");
      writeIndex(readIndex().filter((x) => x.postId !== postId)); // no entry for bytes that may not be there
    }
  });
}

/** The reader lost access to the book: delete every cached version of it. */
function remove(postId: string): Promise<void> {
  postGen.set(postId, (postGen.get(postId) ?? 0) + 1);
  if (!available()) return Promise.resolve();
  return serial(async () => {
    try {
      const cache = await open();
      for (const e of readIndex().filter((x) => x.postId === postId)) await cache.delete(requestKey(e.postId, e.fileId));
      writeIndex(readIndex().filter((e) => e.postId !== postId));
    } catch (e) {
      logger.warn("[bookPdfCache] remove failed", e instanceof Error ? e.name : "error");
    }
  });
}

/** Sign-out: the key goes first (any copy left behind is unreadable), then every cached book (old plain cache too). */
function clear(): Promise<void> {
  globalGen++;
  return serial(async () => {
    try { localStorage.removeItem(INDEX_KEY); localStorage.removeItem(OLD_INDEX_KEY); } catch { /* ignore */ }
    try { await deleteKey(); } catch (e) { logger.warn("[bookPdfCache] deleting the key failed", e instanceof Error ? e.name : "error"); }
    if (!cachesAvailable()) return;
    try {
      await caches.delete(CACHE_NAME);
      await caches.delete(OLD_CACHE_NAME);
    } catch (e) { logger.warn("[bookPdfCache] clear failed", e instanceof Error ? e.name : "error"); }
  });
}

export const bookPdfCache = { available, migrate, stamp, get, put, remove, clear };
